import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-split-view>` says about itself.
 *
 * ── ONE LIST, FOUR BODIES ────────────────────────────────────────────────────
 * The split is one of four pieces that draw the SAME record set four ways: `DataTable` as rows and
 * columns, `CardGrid` as cards, `KanbanBoard` as stages, and this one as a lane of records beside
 * the one being read. The example labelled "The orders list" is the same three records in all four;
 * `insteadUse` says where each of the other three wins.
 *
 * ── ONE RECORD SET, TWO FIELD SETS ───────────────────────────────────────────
 * An item carries `secondary` — the compact fields the LEFT LANE shows under the name — and
 * `detail`, the full set the RIGHT PANE reads. They are separate lists because they answer
 * different questions: the lane's job is to let somebody find the record, the pane's is to let them
 * read it. A `secondary` field whose value is blank or an em dash is dropped from the lane rather
 * than drawn as an empty gap.
 *
 * ── WHAT AN EXAMPLE CANNOT SHOW ──────────────────────────────────────────────
 * Below 720px the two panes collapse to one full-width lane and tapping a card opens the record
 * instead of selecting it. That switch is driven by a media query against the VIEWPORT, so no
 * placement can put the piece into its narrow mode — narrowing the stage does not narrow the
 * window. The behaviour is described under `placing` because there is no way to demonstrate it.
 */
const docs: ComponentDocs = {
  aliases: ['list detail', 'master detail', 'two pane', 'inbox view', 'reading pane', 'console view'],

  answers: [
    'work down a list reading one record at a time',
    'read a record without leaving the list',
    'keep the list in view while looking at a record',
    'open the record properly once reading it is not enough',
  ],

  placements: ['appPage'],

  inputs: [
    {
      name: 'items',
      type: 'json',
      description:
        'The records. Each one an id, the title, the compact secondary fields the left lane shows, ' +
        'and the full detail field set the right pane reads — every value already formatted and ' +
        'every reference already resolved, with an href on a field that should be a link. Set as a ' +
        'property; an attribute cannot hold a list. Handing in a new list keeps the current ' +
        'selection if it is still there, and otherwise falls to the first record.',
    },
  ],

  events: [
    {
      name: 'caos-split-open',
      when:
        'The "Open as tab" control in the detail header is pressed, or — on a narrow viewport, where ' +
        'there is no detail pane — a record in the lane is tapped.',
      carries: '{ id } — the record to open. The host routes it; the split view does not navigate.',
    },
    {
      name: 'caos-navigate',
      when: 'A link inside a detail field is clicked. The default navigation is prevented first.',
      carries: '{ href } — the route the field pointed at.',
    },
  ],

  // NONE, read from the source: both panes are built from `items` and the component renders no
  // <slot>. Nothing can be projected into either side.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-font', controls: 'The typeface of both panes.' },
    { token: '--caos-color-border', controls: 'The rule above the whole piece, and a lane card at rest.' },
    { token: '--caos-color-border-strong', controls: 'The divider between the lane and the detail pane.' },
    { token: '--caos-color-surface', controls: 'The ground of a lane card.' },
    { token: '--caos-color-accent', controls: 'The record names, the links, and the selected card border and rail.' },
    { token: '--caos-accent-wash', controls: 'The tint filling the selected lane card.' },
    { token: '--caos-focus-ring', controls: 'The ring on a focused lane card or link.' },
    { token: '--caos-radius-sm', controls: 'The corners of that ring on a link.' },
    { token: '--caos-color-text-muted', controls: 'The lane field lines, the detail field labels, and "Open as tab".' },
    { token: '--caos-font-display', controls: 'The face of the detail title.' },
    { token: '--caos-font-mono', controls: 'The detail field labels and the "Open as tab" control.' },
    { token: '--caos-color-text', controls: 'The detail field values.' },
    { token: '--caos-radius-sm', controls: 'The corners of a lane card.' },
    { token: '--caos-space-2', controls: 'The padding of the lane, the gap between its cards, and the gap between a card’s name and its fields.' },
    { token: '--caos-space-3', controls: 'The padding inside a lane card, the gap between its field lines, and the gap between the detail title and "Open as tab".' },
    { token: '--caos-space-1', controls: 'The rows between a card’s field lines, the gap inside "Open as tab", and the gap above a detail value.' },
    { token: '--caos-space-4', controls: 'The gap between detail fields, and the least horizontal padding of the detail pane.' },
    { token: '--caos-space-5', controls: 'The top padding of the detail pane, the space above its fields, and the padding of the empty state.' },
    { token: '--caos-space-6', controls: 'The most horizontal padding of the detail pane on a wide screen.' },
  ],

  parts: [
    { part: 'wrap', is: 'the two-pane wrapper' },
    { part: 'lane', is: 'the list lane' },
    { part: 'detail', is: 'the record pane beside it' },
  ],

  methods: [],

  placing:
    'A split is the BODY of a list surface and it takes the page: it sizes its own height against ' +
    'the viewport (at least 420px, up to 900px) and expects to be the last thing in the region, ' +
    'under the header that names the list. Below 720px it collapses to a single full-width lane and ' +
    'tapping a record opens it outright, because there is no room to read one in place — so the ' +
    'surface around it must not assume a detail pane is there.',

  whenToUse:
    'A list somebody works DOWN — triaging, reviewing, checking one record after another — where ' +
    'opening each one properly and coming back would cost more than reading it is worth.',

  insteadUse: [
    {
      name: 'DataTable',
      when:
        'The records are being compared field by field, or need selecting, sorting or totalling — ' +
        'and each one is read on its own page when it comes to that.',
    },
    {
      name: 'CardGrid',
      when: 'The list is browsed rather than worked through, and a name with a couple of rollups is enough.',
    },
    {
      name: 'KanbanBoard',
      when: 'The question is which stage the records are at rather than what any one of them says.',
    },
  ],

  accessibility:
    'Every lane record is a real button, in the tab order and activated by Enter or Space, and the ' +
    'selected one carries aria-current so it is not identified by its tint alone. The links inside ' +
    'detail fields are real anchors with a real href, so they read and behave as links even though ' +
    'the host does the routing. Left to the author: the piece has no landmark or accessible name of ' +
    'its own, so the surface must say what list this is; and selecting a record in the lane rewrites ' +
    'the detail pane without announcing it, so a screen-reader user is told nothing changed.',

  examples: [
    {
      key: 'orders',
      label: 'The orders list',
      summary:
        'The same three records the other three list bodies draw, as a lane beside the one being ' +
        'read: two compact fields per lane card, the full field set in the pane, and the account ' +
        'resolved as a link that reports where it wants to go.',
      placement: {
        component: 'caos-split-view',
        inputs: {
          items: [
            {
              id: 'o1',
              title: 'ORD-4471',
              secondary: [
                { label: 'Account', value: 'Meridian Supply' },
                { label: 'Status', value: 'Draft' },
              ],
              detail: [
                { label: 'Account', value: 'Meridian Supply', href: '/sales-atlas/account/a1' },
                { label: 'Status', value: 'Draft' },
                { label: 'Total', value: '12,400' },
                { label: 'Owner', value: 'Dana Whitfield' },
              ],
            },
            {
              id: 'o2',
              title: 'ORD-4472',
              secondary: [
                { label: 'Account', value: 'Calder & Finch' },
                { label: 'Status', value: 'Approved' },
              ],
              detail: [
                { label: 'Account', value: 'Calder & Finch', href: '/sales-atlas/account/a2' },
                { label: 'Status', value: 'Approved' },
                { label: 'Total', value: '3,150' },
                { label: 'Owner', value: 'Priya Raman' },
              ],
            },
            {
              id: 'o3',
              title: 'ORD-4473',
              secondary: [
                { label: 'Account', value: 'Brightwater Group' },
                { label: 'Status', value: 'In review' },
              ],
              detail: [
                { label: 'Account', value: 'Brightwater Group', href: '/sales-atlas/account/a3' },
                { label: 'Status', value: 'In review' },
                { label: 'Total', value: '86,900' },
                { label: 'Owner', value: 'Dana Whitfield' },
              ],
            },
          ],
        },
      },
    },
    {
      key: 'empty',
      label: 'Nothing in the list',
      summary:
        'An empty lane, which prints one plain muted sentence and leaves the detail pane blank — so ' +
        'a surface that needs to explain why the list is empty has to say it above the split.',
      placement: { component: 'caos-split-view', inputs: { items: [] } },
    },
  ],
};

export default docs;
